import { ADD_USER_DRAWER } from 'src/features/users/constants/drawerConstants';
import _ from 'lodash';
import { connect } from 'react-redux';
import { DELETE_USER_DIALOG } from 'src/features/users/components/DeleteUserDialog/DeleteUserDialog';
import { NEW_DELETE_USER_DIALOG } from 'src/features/users/components/NewDeleteUserDialog/NewDeleteUserDialog';
import { ENABLE_USER_DIALOG } from 'src/features/users/components/EnableUserDialog/EnableUserDialog';
import { AUTHENTICATION_METHOD_USER_DIALOG } from 'src/features/users/components/AuthenticationMethodDialog/AuthenticationMethodDialog';
import { NEW_MANAGE_ROLE_USER_DIALOG } from 'src/features/users/components/NewManageRolesDialog/NewManageRolesDialog';
import { UPDATE_MFA_USER_DIALOG } from 'src/features/users/components/UpdateMfaDialog/UpdateMfaDialog';
import { MANAGE_APPLICATION_USER_DIALOG } from 'src/features/users/components/ManageApplicationDialog/ManageApplicationDialog';
import { RESEND_INVITE_USER_DIALOG } from 'src/features/users/components/ResendInviteUserDialog/ResendInviteUserDialog';
import { loadLoginProvidersWithoutUserCount } from 'src/services/LoginProviderService';
import { defaultPageSize, loadUsersWithConnectedApps, filterUsers } from 'src/services/UsersService';
import { RESET_PASSWORD_USER_DIALOG } from 'src/features/users/components/ResetPasswordUserDialog/ResetPasswordUserDialog';
import { FORGET_MFA_USER_DIALOG } from 'src/features/users/components/ForgetMFAUserDialog/ForgetMFAUserDialog';
import {
    getScreenData,
    Store,
    showDialog,
    setScreenData,
    removeScreenData,
    showDrawer,
    runSaga,
    ACTIVE_SCREEN_KEY,
} from 'cw-ui-core';
import Users from 'src/features/users/components/Users/Users';
import { loadRoles } from 'src/services/RolesService';
import { call, put, takeLatest, cancel } from 'redux-saga/effects';
import updateUsersData from 'src/features/users/utils/UsersDataUtils';
import { DOWNLOAD_USERS_DIALOG } from 'src/features/users/components/DownloadUsersDialog/DownloadUsersDialog';
import {
    selectedIdsMemoized,
    selectedActiveIdsMemoized,
    selectedDisabledIdsMemoized,
    selectedPendingIdsMemoized,
    selectedUsersMemoized,
} from 'src/features/users/components/Users/UsersContainerMemoizedUtils';
import { setIsBulkImportOpen } from 'src/features/bulk-users-import/slice';
import { PERMISSIONS } from 'src/constants';
import { toArray, checkIfUserHaveGivenPermissions } from 'src/util/Util';
import { DISABLE_USER_DIALOG } from '../DisableUserDialog/DisableUserDialog';
import newDesktop from 'src/features/users/components/Users/NewUserColumns';

const mapStateToProps = state => {
    let selectedIds = toArray(getScreenData(state.screen, ['selectedIds', 'filteredUsersToShow']));

    const users = toArray(getScreenData(state.screen, ['usersWithConnectedApps']));
    const isAllUsersReady = getScreenData(state.screen, ['isReady']);
    const updatedUsers = updateUsersData(users, selectedIds);
    const columns = newDesktop;

    const loginProviders = getScreenData(state.screen, ['loginProviders']);
    const searchText = getScreenData(state.screen, ['usersSearchText']);
    const totalCount = getScreenData(state.screen, ['totalCount']);
    const currentPageIndex = getScreenData(state.screen, ['currentPageIndex']);
    const pageCount = getScreenData(state.screen, ['pageCount']);
    selectedIds = selectedIdsMemoized(state);
    const selectedActiveIds = selectedActiveIdsMemoized(state);
    const selectedDisabledIds = selectedDisabledIdsMemoized(state);
    const selectedPendingIds = selectedPendingIdsMemoized(state);
    const selectedUsers = selectedUsersMemoized(state);
    const autoResetSelectedRows = getScreenData(state.screen, ['autoResetSelectedRows']);

    const usersFiltered = checkIfUserHaveGivenPermissions([PERMISSIONS.USERS_VIEW]) ? (updatedUsers ?? users) : [];

    return {
        isLoading: !isAllUsersReady,
        disabledOnExternalAuthSelect: users.filter(u => u.loginScheme !== null).some(v => selectedIds.includes(v.id)),
        disabledOnMultiSelect: selectedIds.length !== 1,
        disabledOnNoneSelect: selectedIds.length === 0,
        disabledUnlessActiveSelect: selectedActiveIds ? selectedActiveIds.length === 0 : true,
        disabledUnlessPendingSelect: selectedPendingIds ? selectedPendingIds.length === 0 : true,
        disabledUnlessDisabledSelect: !_.every(selectedUsers, user => user.status === 'disabled'),
        isUserAuditInProgress: getScreenData(state.screen, ['isUserAuditInProgress']),
        disableWhenActiveSelected: !!(selectedActiveIds && selectedActiveIds.length > 0),
        disabledWhenDisableSelected: selectedDisabledIds.length > 0,
        loginProviders: loginProviders ? loginProviders.toJS() : [],
        searchText,
        selectedUsers,
        users,
        usersFiltered,
        columns,
        totalCount: totalCount ?? users.length,
        currentPageIndex: currentPageIndex ?? 0,
        pageCount: pageCount ?? 1,
        pageSize: defaultPageSize,
        autoResetSelectedRows,
    };
};

const mapDispatchToProps = dispatch => {
    let takeLatestTaskForFilterUsers;
    return {
        onDidMount: async () => {
            runSaga(loadUsersWithConnectedApps);
            runSaga(function* () {
                takeLatestTaskForFilterUsers = yield takeLatest('FILTER_USERS', filterUsers, ACTIVE_SCREEN_KEY, false);
            });

            await Store().runSaga(function* () {
                yield put(setScreenData(['isProviderReady'], false));
                yield call(loadLoginProvidersWithoutUserCount);
                yield put(setScreenData(['isProviderReady'], true));
            });

            await Store().runSaga(loadRoles);
            dispatch(setScreenData(['usersSearchText'], ''));
            dispatch(removeScreenData(['addFilter']));
        },
        componentWillUnmount: () => {
            if (takeLatestTaskForFilterUsers && takeLatestTaskForFilterUsers.isRunning()) {
                Store().runSaga(function* () {
                    yield cancel(takeLatestTaskForFilterUsers);
                });
            }
            dispatch(setScreenData(['usersSearchText'], ''));
            dispatch(removeScreenData(['addFilter']));
            dispatch(removeScreenData(['usersWithConnectedApps']));
            dispatch(removeScreenData(['selectedIds', 'filteredUsersToShow']));
        },
        changePage: async pageIndex => {
            const searchText = getScreenData(Store().getState().screen, ['usersSearchText']);
            const filters = toArray(getScreenData(Store().getState().screen, ['addFilter']));
            runSaga(loadUsersWithConnectedApps, pageIndex + 1, 50, searchText, filters);
        },
        onAddUser: () => {
            dispatch(showDrawer(ADD_USER_DRAWER));
        },
        onBulkImportUser: () => {
            dispatch(setIsBulkImportOpen());
        },
        isRowHighlighted: row => {
            const userId = getScreenData(Store().getState().screen, ['activeUserId']);
            return userId && row.id === userId;
        },
        onRowSelected: rows => {
            const selectedIds = rows.map(row => row.original.id);
            dispatch(setScreenData(['selectedIds', 'filteredUsersToShow'], selectedIds));
        },
        onForgetMFA: () => {
            const { screen } = Store().getState();
            const users = toArray(getScreenData(screen, ['allUsers']));
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            const selectedIdsWithMFA = users
                .filter(x => selectedIds.includes(x.id) && x.mfaEnabled && !x.isPending)
                .map(v => v.id);

            dispatch(
                showDialog(FORGET_MFA_USER_DIALOG, {
                    selectedIdsWithMFA,
                })
            );
        },
        onResetPassword: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(
                showDialog(RESET_PASSWORD_USER_DIALOG, {
                    selectedIdsForResetPassword: selectedIds,
                })
            );
        },
        onAuthMethodSelect: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(
                showDialog(AUTHENTICATION_METHOD_USER_DIALOG, {
                    selectedIdsForAuth: selectedIds,
                })
            );
        },
        onUpdateMfaMethodSelect: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(
                showDialog(UPDATE_MFA_USER_DIALOG, {
                    selectedIdsForAuth: selectedIds,
                })
            );
        },
        onNewManageRolesSelect: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(
                showDialog(NEW_MANAGE_ROLE_USER_DIALOG, {
                    selectedIdsForRoles: selectedIds,
                    originAccount: false,
                })
            );
        },
        onManageApplicationSelect: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(
                showDialog(MANAGE_APPLICATION_USER_DIALOG, {
                    selectedIdsForApplications: selectedIds,
                })
            );
        },
        onDeleteAccount: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(showDialog(DELETE_USER_DIALOG, { selectedIdsToDelete: selectedIds }));
        },

        onDisableAccount: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(showDialog(DISABLE_USER_DIALOG, { selectedIdsToDisable: selectedIds }));
        },
        onNewDeleteAccount: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(showDialog(NEW_DELETE_USER_DIALOG, { selectedIdsToDelete: selectedIds }));
        },
        onEnableAccount: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(showDialog(ENABLE_USER_DIALOG, { selectedIdsToEnable: selectedIds }));
        },
        onResendInvite: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(
                showDialog(RESEND_INVITE_USER_DIALOG, {
                    selectedIdsForResendInvite: selectedIds,
                })
            );
        },
        onDownloadUsersCsv: () => {
            const { screen } = Store().getState();
            const selectedIds = toArray(getScreenData(screen, ['selectedIds', 'filteredUsersToShow']));
            dispatch(
                showDialog(DOWNLOAD_USERS_DIALOG, {
                    selectedIdsForExport: selectedIds,
                    originAccount: false,
                })
            );
        },
        filterList: text => {
            dispatch(setScreenData(['usersSearchText'], text));
            runSaga(function* () {
                yield put({ type: 'FILTER_USERS' });
            });
        },
        onInfoIconClick: () => {
            window.open('https://docs.connectwise.com/?cid=CW_Home_SSO_Users&psa=1');
        },
    };
};

const UsersContainer = connect(mapStateToProps, mapDispatchToProps)(Users);

export default UsersContainer;
